import { AiOrderExplanation } from '../../../app/services/api/orderManagementClient';

type JsonObject = Record<string, unknown>;

export type ParsedAiOrderExplanation = AiOrderExplanation & {
  replenishmentCycleDays?: number | null;
  effectiveSafetyStock?: number | null;
  targetStockLevel?: number | null;
  projectedStockOnHand?: number | null;
  rawOrderQuantity?: number | null;
  orderQuantityAfterMaxStock?: number | null;
  orderQuantityAfterMaxOrder?: number | null;
  orderableQuantityBeforeRounding?: number | null;
  recommendedQuantityInPrimaryUom?: number | null;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isOptionalFiniteNumber = (value: unknown) =>
  value == null || isFiniteNumber(value);

const getValue = (value: JsonObject, camelCaseName: string, pascalCaseName: string) =>
  value[camelCaseName] ?? value[pascalCaseName];

const normalizeCapturedAt = (value: unknown): string | null => {
  if (typeof value !== 'string' || value.length === 0) return null;

  // WCF DataContractJsonSerializer emits DateTime values as /Date(ms)/ (optionally with an
  // offset suffix). Convert that legacy wire representation into ISO so Day.js can render the
  // recorded timestamp consistently. Persisted Json.NET snapshots already use ISO strings.
  const wcfDateMatch = /^\/Date\((-?\d+)([+-]\d{4})?\)\/$/.exec(value);
  if (!wcfDateMatch) return value;

  const date = new Date(Number(wcfDateMatch[1]));
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const parseAiOrderExplanation = (value: unknown): ParsedAiOrderExplanation | null => {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null;

  const payload = value as JsonObject;
  const deliveryDate = getValue(payload, 'deliveryDate', 'DeliveryDate');
  const capturedAt = normalizeCapturedAt(getValue(payload, 'capturedAt', 'CapturedAt'));
  const stockOnHand = getValue(payload, 'stockOnHand', 'StockOnHand');
  const stockOnOrder = getValue(payload, 'stockOnOrder', 'StockOnOrder');
  const predictedDemandQuantity = getValue(
    payload,
    'predictedDemandQuantity',
    'PredictedDemandQuantity',
  );
  const safetyStock = getValue(payload, 'safetyStock', 'SafetyStock');
  const minStockOnHandQuantity = getValue(
    payload,
    'minStockOnHandQuantity',
    'MinStockOnHandQuantity',
  );
  const maxStockOnHandQuantity = getValue(
    payload,
    'maxStockOnHandQuantity',
    'MaxStockOnHandQuantity',
  );
  const maxOrderQuantity = getValue(payload, 'maxOrderQuantity', 'MaxOrderQuantity');
  const uomConversionFactor = getValue(payload, 'uomConversionFactor', 'UomConversionFactor');
  const recommendedQuantity = getValue(payload, 'recommendedQuantity', 'RecommendedQuantity');
  const isQuantityOverridden = getValue(payload, 'isQuantityOverridden', 'IsQuantityOverridden');
  const replenishmentCycleDays = getValue(payload, 'replenishmentCycleDays', 'ReplenishmentCycleDays');
  const effectiveSafetyStock = getValue(payload, 'effectiveSafetyStock', 'EffectiveSafetyStock');
  const targetStockLevel = getValue(payload, 'targetStockLevel', 'TargetStockLevel');
  const projectedStockOnHand = getValue(payload, 'projectedStockOnHand', 'ProjectedStockOnHand');
  const rawOrderQuantity = getValue(payload, 'rawOrderQuantity', 'RawOrderQuantity');
  const orderQuantityAfterMaxStock = getValue(
    payload,
    'orderQuantityAfterMaxStock',
    'OrderQuantityAfterMaxStock',
  );
  const orderQuantityAfterMaxOrder = getValue(
    payload,
    'orderQuantityAfterMaxOrder',
    'OrderQuantityAfterMaxOrder',
  );
  const orderableQuantityBeforeRounding = getValue(
    payload,
    'orderableQuantityBeforeRounding',
    'OrderableQuantityBeforeRounding',
  );
  const recommendedQuantityInPrimaryUom = getValue(
    payload,
    'recommendedQuantityInPrimaryUom',
    'RecommendedQuantityInPrimaryUom',
  );
  const rawProjection = getValue(payload, 'projection', 'Projection');

  if (
    typeof deliveryDate !== 'string' ||
    deliveryDate.length === 0 ||
    capturedAt == null ||
    !isFiniteNumber(stockOnHand) ||
    !isFiniteNumber(stockOnOrder) ||
    !isFiniteNumber(predictedDemandQuantity) ||
    !isFiniteNumber(uomConversionFactor) ||
    !isFiniteNumber(recommendedQuantity) ||
    (isQuantityOverridden != null && typeof isQuantityOverridden !== 'boolean') ||
    !isOptionalFiniteNumber(safetyStock) ||
    !isOptionalFiniteNumber(minStockOnHandQuantity) ||
    !isOptionalFiniteNumber(maxStockOnHandQuantity) ||
    !isOptionalFiniteNumber(maxOrderQuantity) ||
    !isOptionalFiniteNumber(replenishmentCycleDays) ||
    !isOptionalFiniteNumber(effectiveSafetyStock) ||
    !isOptionalFiniteNumber(targetStockLevel) ||
    !isOptionalFiniteNumber(projectedStockOnHand) ||
    !isOptionalFiniteNumber(rawOrderQuantity) ||
    !isOptionalFiniteNumber(orderQuantityAfterMaxStock) ||
    !isOptionalFiniteNumber(orderQuantityAfterMaxOrder) ||
    !isOptionalFiniteNumber(orderableQuantityBeforeRounding) ||
    !isOptionalFiniteNumber(recommendedQuantityInPrimaryUom)
  ) {
    return null;
  }

  let projection: AiOrderExplanation['projection'];
  if (rawProjection != null) {
    if (!Array.isArray(rawProjection)) return null;

    const normalizedProjection = rawProjection.map((row) => {
      if (row == null || typeof row !== 'object' || Array.isArray(row)) return null;

      const projectionRow = row as JsonObject;
      const date = getValue(projectionRow, 'date', 'Date');
      const quantityOnHand = getValue(projectionRow, 'quantityOnHand', 'QuantityOnHand');
      const incomingQuantity = getValue(projectionRow, 'incomingQuantity', 'IncomingQuantity');
      const demand = getValue(
        projectionRow,
        'predictedDemandQuantity',
        'PredictedDemandQuantity',
      );

      if (
        typeof date !== 'string' ||
        date.length === 0 ||
        !isFiniteNumber(quantityOnHand) ||
        !isFiniteNumber(incomingQuantity) ||
        !isFiniteNumber(demand)
      ) {
        return null;
      }

      return {
        date,
        quantityOnHand,
        incomingQuantity,
        predictedDemandQuantity: demand,
      };
    });

    if (normalizedProjection.some((row) => row == null)) return null;
    projection = normalizedProjection as AiOrderExplanation['projection'];
  }

  return {
    deliveryDate,
    capturedAt,
    stockOnHand,
    stockOnOrder,
    predictedDemandQuantity,
    safetyStock: safetyStock as number | null | undefined,
    minStockOnHandQuantity: minStockOnHandQuantity as number | null | undefined,
    maxStockOnHandQuantity: maxStockOnHandQuantity as number | null | undefined,
    maxOrderQuantity: maxOrderQuantity as number | null | undefined,
    uomConversionFactor,
    recommendedQuantity,
    isQuantityOverridden: isQuantityOverridden as boolean | null | undefined,
    replenishmentCycleDays: replenishmentCycleDays as number | null | undefined,
    effectiveSafetyStock: effectiveSafetyStock as number | null | undefined,
    targetStockLevel: targetStockLevel as number | null | undefined,
    projectedStockOnHand: projectedStockOnHand as number | null | undefined,
    rawOrderQuantity: rawOrderQuantity as number | null | undefined,
    orderQuantityAfterMaxStock: orderQuantityAfterMaxStock as number | null | undefined,
    orderQuantityAfterMaxOrder: orderQuantityAfterMaxOrder as number | null | undefined,
    orderableQuantityBeforeRounding: orderableQuantityBeforeRounding as number | null | undefined,
    recommendedQuantityInPrimaryUom: recommendedQuantityInPrimaryUom as number | null | undefined,
    projection,
  };
};
